import { useMemo } from 'react'
import { TreeNodeOrNodeSlug, useIsChatEnabled } from 'react-common'
import { useTranslation } from 'react-i18next'
import { useNotificationIndicatorSidebarItem } from '@/features/chat/useNotificationIndicatorSidebarItem'
import { generatePathWithRoot } from '@/features/layout/utils/generatePathWithRoot'
import { useSetActiveSlug } from '@/features/stencil/stubs'
import { useIsCatalogCourse } from '@/hooks/useIsCatalogCourse'
import { courseViewBaseUrl } from '@/Router'
import { COURSE_CONTENT_SLUG, COURSE_DETAILS_SLUG } from '@/views/courses/ActivityTypes/hooks/useSetCourseSidebarLayers'

export const useGetCourseDetailsSteps = ({
  isLti,
  inProgress,
  objectiveId,
  supportReview,
}: {
  isLti: boolean
  inProgress: boolean
  objectiveId: string
  supportReview: boolean
}) => {
  useSetActiveSlug(COURSE_DETAILS_SLUG)
  const isCatalogCourse = useIsCatalogCourse()
  const notificationIndicator = useNotificationIndicatorSidebarItem()
  const isChatEnabled = useIsChatEnabled()

  const { t } = useTranslation()
  const sidebarLayers = useMemo(() => {
    if (isCatalogCourse) {
      return [
        {
          name: t('resourcesWizard.details'),
          url: '', // If course is not started, no need to give url, because there is only one layer at menu
          slug: COURSE_DETAILS_SLUG,
          icon_id: 'InformationInCircleBold',
        },
      ] as TreeNodeOrNodeSlug[]
    }

    const detailsUrl = isLti ? '' : generatePathWithRoot(`${courseViewBaseUrl}details`, { objectiveId })

    const array: TreeNodeOrNodeSlug[] = [
      {
        name: t('resourcesWizard.details'),
        url: inProgress ? detailsUrl : '', // If course is not started, no need to give url, because there is only one layer at menu
        slug: COURSE_DETAILS_SLUG,
        icon_id: 'InformationInCircleBold',
      },
    ]

    if (!isLti && (inProgress || supportReview)) {
      array.push({
        name: t('resourcesWizard.content'),
        url: generatePathWithRoot(`${courseViewBaseUrl}content`, { objectiveId }),
        slug: COURSE_CONTENT_SLUG,
        icon_id: 'FileBoldStroke',
      } as TreeNodeOrNodeSlug)
    }

    if (isChatEnabled) {
      array.push(notificationIndicator)
    }

    return array
  }, [isCatalogCourse, isLti, objectiveId, t, inProgress, supportReview, isChatEnabled, notificationIndicator])

  return { sidebarLayers }
}
